'use client';

import { useEffect } from 'react';

import { type AppEvent, useAppEvents } from '@kit/shared/events';
import { isBrowser } from '@kit/shared/utils';

import type { ChatEventTypes } from '~/(chat)/_lib/chat-events';
import { recordConfidentialUsage } from '~/(chat)/chat/_lib/confidential-usage-logger';
import { hashFileName } from '~/(chat)/chat/_lib/hash-file-name';

type ChatEvent<K extends keyof ChatEventTypes> = AppEvent<ChatEventTypes, K>;

/**
 * Subscribes to confidential-mode app events and persists anonymized usage
 * telemetry to the database (via the usage logger). This is the database
 * counterpart to the analytics provider: the same events that flow to the
 * analytics service are mirrored into `confidential_usage_events` so the admin
 * conversation inspector and the confidential-usage dashboard can surface them.
 *
 * Only confidential-mode activity is captured — regular (non-private) file
 * uploads already live in `chat_attachments` and are skipped here.
 */
function ConfidentialUsageProviderBrowser(props: React.PropsWithChildren) {
  const appEvents = useAppEvents<ChatEventTypes>();

  useEffect(() => {
    const onActivated = (event: ChatEvent<'confidential-mode-activated'>) =>
      recordConfidentialUsage({
        eventType: 'mode_activated',
        providerMode: event.payload.mode || 'unknown',
      });

    const onClosed = () =>
      recordConfidentialUsage({ eventType: 'mode_closed' });

    const onFileUploaded = (event: ChatEvent<'file-uploaded'>) => {
      // Only confidential-mode uploads — regular uploads are already persisted
      // in chat_attachments.
      if (event.payload.isPrivateMode !== 'true') return;

      const fileSize = Number(event.payload.fileSize) || 0;

      void hashFileName(event.payload.fileName).then((fileNameHash) =>
        recordConfidentialUsage({
          eventType: 'file_uploaded',
          fileNameHash,
          fileType: event.payload.fileType || 'unknown',
          fileSize,
        }),
      );
    };

    const onExported = (event: ChatEvent<'confidential-data-exported'>) =>
      recordConfidentialUsage({
        eventType: 'data_exported',
        chatCount: event.payload.chatCount,
        blobCount: event.payload.blobCount,
      });

    const onImported = (event: ChatEvent<'confidential-data-imported'>) =>
      recordConfidentialUsage({
        eventType: 'data_imported',
        chatCount: event.payload.chatCount,
        blobCount: event.payload.blobCount,
      });

    const onMessageSent = (event: ChatEvent<'confidential-message-sent'>) =>
      recordConfidentialUsage({
        eventType: 'message_sent',
        providerMode: event.payload.providerMode,
        modelId: event.payload.modelId,
        assistantId: event.payload.assistantId,
        chatMode: event.payload.chatMode,
        webSearchUsed: event.payload.webSearchUsed,
        knowledgeBaseIds: event.payload.knowledgeBaseIds,
      });

    appEvents.on('confidential-mode-activated', onActivated);
    appEvents.on('confidential-mode-closed', onClosed);
    appEvents.on('file-uploaded', onFileUploaded);
    appEvents.on('confidential-data-exported', onExported);
    appEvents.on('confidential-data-imported', onImported);
    appEvents.on('confidential-message-sent', onMessageSent);

    return () => {
      appEvents.off('confidential-mode-activated', onActivated);
      appEvents.off('confidential-mode-closed', onClosed);
      appEvents.off('file-uploaded', onFileUploaded);
      appEvents.off('confidential-data-exported', onExported);
      appEvents.off('confidential-data-imported', onImported);
      appEvents.off('confidential-message-sent', onMessageSent);
    };
  }, [appEvents]);

  return props.children;
}

export function ConfidentialUsageProvider(props: React.PropsWithChildren) {
  if (!isBrowser()) {
    return props.children;
  }

  return (
    <ConfidentialUsageProviderBrowser>
      {props.children}
    </ConfidentialUsageProviderBrowser>
  );
}
